import React, { useState, useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";
import { getIcon } from "@/lib/icons";
import api from "@/lib/api";

const PALETTE = [
  { bg: "#eef9f0", color: "#198530" },
  { bg: "#eff6ff", color: "#1d4ed8" },
  { bg: "#fff7ed", color: "#ea580c" },
  { bg: "#fdf4ff", color: "#7c3aed" },
  { bg: "#ecfeff", color: "#0e7490" },
  { bg: "#fefce8", color: "#ca8a04" },
  { bg: "#f0fdf4", color: "#16a34a" },
  { bg: "#fff1f2", color: "#be123c" },
];

const FALLBACK_FACILITIES = [
  { name: "Masjid / Musholla", desc: "Tempat ibadah dan pembelajaran agama yang nyaman, bersih, dan kondusif bagi seluruh santri dan guru.", icon: "Building2", img: "https://images.unsplash.com/photo-1683884669701-0636fb4d1523?w=500&q=75" },
  { name: "Ruang Kelas Nyaman", desc: "Ruang belajar yang kondusif, bersih, berventilasi baik, dan dilengkapi peralatan belajar yang memadai.", icon: "BookOpen", img: "https://images.unsplash.com/photo-1589104760192-ccab0ce0d90f?w=500&q=75" },
  { name: "Lapangan Olahraga", desc: "Fasilitas olahraga untuk kegiatan pramuka, sepak bola, renang, dan berbagai aktivitas fisik lainnya.", icon: "Dumbbell", img: "" },
  { name: "Perpustakaan", desc: "Koleksi lengkap buku pelajaran, kitab Islam, ensiklopedi, dan buku pengetahuan umum untuk siswa.", icon: "BookOpen", img: "https://images.unsplash.com/photo-1638040895680-59de2786b189?w=500&q=75" },
];

// Komponen card fasilitas dengan truncate "Selengkapnya"
function FacilityCard({ facility, idx }) {
  const [expanded, setExpanded] = useState(false);
  const pal = PALETTE[idx % PALETTE.length];
  const Icon = getIcon(facility.icon);
  const MAX_CHARS = 70;
  const isLong = facility.desc && facility.desc.length > MAX_CHARS;
  const displayDesc = expanded || !isLong ? facility.desc : facility.desc.slice(0, MAX_CHARS) + "...";

  return (
    <div
      data-testid={`facility-card-${idx}`}
      className="bg-white rounded-xl overflow-hidden transition-all duration-300"
      style={{ border: "1px solid #f3f4f6" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 10px 24px rgba(0,0,0,0.09)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {facility.img ? (
        <div className="overflow-hidden" style={{ height: "120px" }}>
          <img
            src={facility.img}
            alt={facility.name}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            loading="lazy"
          />
        </div>
      ) : (
        <div className="flex items-center justify-center" style={{ height: "120px", backgroundColor: pal.bg }}>
          <Icon size={44} style={{ color: pal.color }} />
        </div>
      )}
      <div className="p-3 sm:p-4">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center mb-2"
          style={{ backgroundColor: pal.bg }}
        >
          <Icon size={16} style={{ color: pal.color }} />
        </div>
        <h3 className="font-bold text-gray-900 mb-1.5" style={{ fontSize: "0.78rem" }}>{facility.name}</h3>
        <p className="text-gray-500 leading-relaxed" style={{ fontSize: "0.7rem" }}>
          {displayDesc}
        </p>
        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1.5 font-semibold text-xs"
            style={{ color: "#198530" }}
          >
            {expanded ? "Lebih Sedikit" : "Selengkapnya"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function FasilitasSekolah() {
  const { settings, waUrl } = useSettings();
  const [mapsAddress, setMapsAddress] = useState("Namorambe, Deli Serdang, Sumatera Utara");
  const facilities = settings?.facilities?.length ? settings.facilities : FALLBACK_FACILITIES;

  useEffect(() => {
    api.get("/registration-info")
      .then((res) => { if (res.data && res.data.tempat) setMapsAddress(res.data.tempat); })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="fasilitas-page" style={{ paddingTop: "60px" }}>
      {/* Header - ringkas di mobile */}
      <div
        className="py-8 sm:py-14 text-white text-center relative overflow-hidden"
        style={{ backgroundColor: "#198530" }}
      >
        <div className="absolute inset-0" style={{ opacity: 0.05 }}>
          <svg width="100%" height="100%">
            <defs>
              <pattern id="dots4" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
                <circle cx="15" cy="15" r="2" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots4)" />
          </svg>
        </div>
        <div className="relative z-10 px-4">
          <p className="text-xs sm:text-sm uppercase tracking-widest mb-1" style={{ color: "#d4af37" }}>Sarana & Prasarana</p>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold">Fasilitas Sekolah</h1>
          <p className="mt-2 text-xs sm:text-sm md:text-base" style={{ color: "#bbf7d0" }}>
            Sarana dan prasarana pendukung proses belajar mengajar di Yayasan Tozan
          </p>
        </div>
      </div>

      {/* Facilities Grid - 2 kolom di mobile */}
      <section className="py-8 sm:py-14" style={{ backgroundColor: "#F8FAF8" }}>
        <div className="max-w-6xl mx-auto px-3 sm:px-4">
          <div className="text-center mb-6 sm:mb-10">
            <p className="text-xs sm:text-sm uppercase tracking-widest font-semibold mb-1" style={{ color: "#d4af37" }}>
              Fasilitas Unggulan
            </p>
            <h2 className="text-xl sm:text-3xl font-bold text-gray-900">
              Lingkungan Belajar yang Kondusif
            </h2>
            <p className="text-gray-500 mt-2 max-w-2xl mx-auto text-xs sm:text-sm md:text-base">
              Yayasan Tozan menyediakan fasilitas lengkap untuk mendukung tumbuh kembang siswa
              secara fisik, intelektual, dan spiritual.
            </p>
          </div>

          {/* 2 kolom mobile, 4 kolom desktop */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {facilities.map((facility, idx) => (
              <FacilityCard key={idx} facility={facility} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* Islamic Environment Section */}
      <section className="py-10 sm:py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7 sm:gap-10 items-center">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-widest font-semibold mb-2" style={{ color: "#d4af37" }}>
                Lingkungan Islami
              </p>
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-3">
                Suasana Belajar yang Bernuansa Islam
              </h2>
              <div className="space-y-3 text-xs sm:text-sm text-gray-600 leading-relaxed">
                <p>
                  Seluruh fasilitas di Yayasan Tozan dirancang untuk menciptakan lingkungan belajar yang
                  Islami, kondusif, dan menyenangkan. Dekorasi dan suasana sekolah mencerminkan
                  nilai-nilai Islam yang positif.
                </p>
                <p>
                  Kegiatan sehari-hari dimulai dengan shalat dhuha berjamaah, pembacaan Al-Qur'an,
                  dan dzikir pagi. Hal ini menciptakan atmosfer spiritual yang mendukung pembentukan
                  karakter Islami pada diri setiap siswa.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden" style={{ border: "2px solid #d4af37", height: "280px" }}>
              <iframe
                src={`https://maps.google.com/maps?q=${encodeURIComponent(mapsAddress)}&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Lokasi Yayasan Tozan"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 sm:py-12" style={{ backgroundColor: "#044712" }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-xl sm:text-2xl mb-2" style={{ fontFamily: "'Amiri', serif", color: "#d4af37" }}>
            {"\u0631\u064e\u0628\u0650\u0651 \u0632\u0650\u062f\u0652\u0646\u0650\u064a \u0639\u0650\u0644\u0652\u0645\u064b\u0627"}
          </p>
          <h2 className="text-xl sm:text-3xl font-bold text-white mb-3">Tertarik Mendaftarkan Anak Anda?</h2>
          <p className="mb-6 text-xs sm:text-sm leading-relaxed" style={{ color: "#bbf7d0" }}>
            Berikan anak Anda pendidikan Islam terbaik di Yayasan Tozan. Hubungi kami
            sekarang untuk informasi lebih lanjut tentang fasilitas dan program unggulan kami.
          </p>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="facilities-wa-button"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-lg transition-all hover:opacity-90 hover:scale-105"
            style={{ backgroundColor: "#d4af37", color: "#044712" }}
          >
            Hubungi Kami via WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
}
